import * as React from 'react';
import { Banner, Button, Flex, FlexItem, Spinner } from '@patternfly/react-core';
import { ExclamationCircleIcon } from '@patternfly/react-icons';
import { pingApi } from '@app/api';
import { useApiHealthStore } from '@app/store/apiHealthStore';
import { SERVICE_UNREACHABLE_MESSAGE } from '@app/utils/ApiError';

const FIRST_RETRY_MS = 5000;
const MAX_RETRY_MS = 60000;

/**
 * Tier 1 of API error handling: a persistent, non-blocking banner shown while the
 * backend is unreachable.
 *
 * Deliberately not a modal or a backdrop — the user keeps their navigation and any
 * data already cached in the stores, and gets one honest explanation that stays put
 * instead of a toast that disappears while the outage continues.
 */
const ApiHealthBanner: React.FunctionComponent = () => {
  const status = useApiHealthStore((state) => state.status);
  const failureCount = useApiHealthStore((state) => state.failureCount);
  const isProbing = useApiHealthStore((state) => state.isProbing);
  const lastError = useApiHealthStore((state) => state.lastError);

  const probe = React.useCallback(async () => {
    useApiHealthStore.getState().setProbing(true);
    try {
      // The result is reported to the health store by fetchData; a success flips the
      // status to online (which unmounts this banner and re-triggers failed loads).
      await pingApi();
    } catch {
      // Already recorded — nothing to do here but stop showing the spinner.
    } finally {
      useApiHealthStore.getState().setProbing(false);
    }
  }, []);

  // Back off while the service stays down: 5s, 10s, 20s… capped at a minute.
  React.useEffect(() => {
    if (status !== 'offline') return;

    const delay = Math.min(FIRST_RETRY_MS * 2 ** Math.max(0, failureCount - 1), MAX_RETRY_MS);
    const timer = window.setTimeout(() => void probe(), delay);

    return () => window.clearTimeout(timer);
  }, [status, failureCount, probe]);

  if (status !== 'offline') return null;

  return (
    <Banner status="danger" isSticky screenReaderText="Service unavailable">
      <Flex alignItems={{ default: 'alignItemsCenter' }} spaceItems={{ default: 'spaceItemsSm' }}>
        <FlexItem>
          <ExclamationCircleIcon />
        </FlexItem>
        <FlexItem>
          <strong>The allocation service is not responding.</strong>{' '}
          {/* Only worth repeating when the service told us something more specific */}
          {lastError && lastError !== SERVICE_UNREACHABLE_MESSAGE ? `${lastError} ` : ''}
          Data on screen may be incomplete or out of date, and changes cannot be saved until the
          connection is restored.
        </FlexItem>
        <FlexItem>
          {isProbing ? (
            <Flex alignItems={{ default: 'alignItemsCenter' }} spaceItems={{ default: 'spaceItemsXs' }}>
              <FlexItem><Spinner size="sm" /></FlexItem>
              <FlexItem>Reconnecting…</FlexItem>
            </Flex>
          ) : (
            <Button variant="link" isInline onClick={() => void probe()}>
              Retry now
            </Button>
          )}
        </FlexItem>
      </Flex>
    </Banner>
  );
};

export { ApiHealthBanner };
